import { type Toast, type ToastInput, type ToastTone } from './toast-store.types';

/** At most four at once; older ones are dropped rather than stacked off-screen. */
const MAX_VISIBLE = 4;

const DEFAULT_DURATION = 4000;

/**
 * Per-tone lifetime.
 *
 * `error` and `loading` are PINNED. A failure the operator scrolled past is a
 * failure they will cause again, and a pending toast has no idea how long its
 * mutation will take — a four-second "Working…" that vanishes mid-request is
 * worse than none.
 */
function defaultDuration(tone: ToastTone): number | null {
  return tone === 'error' || tone === 'loading' ? null : DEFAULT_DURATION;
}

let toasts: Toast[] = [];
let sequence = 0;

const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function emit(): void {
  for (const listener of listeners) {
    listener();
  }
}

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

function scheduleDismiss(id: string, duration: number | null): void {
  clearTimer(id);
  if (duration === null) return;

  timers.set(
    id,
    setTimeout(() => {
      toastStore.dismiss(id);
    }, duration),
  );
}

/**
 * The toast store, as an external store rather than a redux slice.
 *
 * Two reasons. A toast holds a timer handle and is dismissed from a timeout,
 * neither of which belongs in a serializable reducer; and a toast changing must
 * re-render the `Toaster` alone, not every connected component in the shell.
 *
 * `getSnapshot` returns the SAME array reference until something actually
 * changes. That is the `useSyncExternalStore` contract, and breaking it does not
 * fail loudly — React re-renders in a loop and eventually throws "The result of
 * getSnapshot should be cached", usually far from the cause.
 *
 * IDs are a counter, not `Math.random()`/`Date.now()`: the React Compiler rules
 * ban both during render, and a counter is deterministic under test.
 */
export const toastStore = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  getSnapshot(): Toast[] {
    return toasts;
  },

  push(input: ToastInput): string {
    sequence += 1;
    const id = `toast-${String(sequence)}`;
    const duration = input.duration === undefined ? defaultDuration(input.tone) : input.duration;

    toasts = [...toasts, { ...input, id, duration }].slice(-MAX_VISIBLE);
    scheduleDismiss(id, duration);
    emit();

    return id;
  },

  /**
   * Resolves a toast in place — the "Working… → Saved" transition.
   *
   * The duration is recomputed from the NEW tone unless the caller overrides it,
   * so a pinned pending toast starts its clock the moment it becomes a success.
   */
  update(id: string, patch: Partial<ToastInput>): void {
    const index = toasts.findIndex((toast) => toast.id === id);
    if (index === -1) return;

    const previous = toasts[index];
    if (previous === undefined) return;

    const tone = patch.tone ?? previous.tone;
    const duration =
      patch.duration === undefined
        ? patch.tone === undefined
          ? previous.duration
          : defaultDuration(tone)
        : patch.duration;

    const next = { ...previous, ...patch, tone, duration, id };
    toasts = toasts.map((toast, position) => (position === index ? next : toast));
    scheduleDismiss(id, duration);
    emit();
  },

  dismiss(id: string): void {
    clearTimer(id);
    const next = toasts.filter((toast) => toast.id !== id);
    // Emitting when nothing changed would break the stable-snapshot contract
    // for every other subscriber.
    if (next.length === toasts.length) return;

    toasts = next;
    emit();
  },

  /** Test seam, and the sign-out path. */
  reset(): void {
    for (const id of [...timers.keys()]) {
      clearTimer(id);
    }
    toasts = [];
    sequence = 0;
    emit();
  },
};
